import type { ReactElement } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { InventoryUserListDialog } from '@/pages/organization/components/InventoryUserListDialog';
import type { SkillDataTableItem } from '@kirin/api-client/contracts/skills';

interface SkillListModalProps {
  /**
   * The row being shown. Kept by the caller ACROSS the close, so the dialog
   * has something to render while it animates out — reading a row that was
   * nulled on close would blank the title to "(0)" and flash the empty
   * state on the way out. `open` alone decides visibility.
   */
  row: SkillDataTableItem | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Projects / Users drilldowns for a skill row (KIR-4699).
 *
 * Unlike the AI-Instructions modals these fetch nothing: a skill row already
 * carries its full `projects` and `users` arrays, so there is no second
 * request and no loading state to get wrong.
 *
 * They deliberately do NOT reuse `InstructionProjectListModal` /
 * `InstructionUserListModal`, which key off a single `contentId`. A skill is
 * an aggregate over every file that shares its `(name, source, project)` —
 * often several `contentId`s across endpoints — so the content-scoped
 * endpoint would answer for one file and under-report the skill.
 */
export function SkillProjectsModal({
  row,
  open,
  onClose,
}: SkillListModalProps): ReactElement {
  const projects = row?.projects ?? [];
  // Project names are repo basenames, so two rows here can legitimately read
  // "api". The origin is shown only on the ones that actually collide —
  // adding it to every row would be noise on the common case.
  const clashing = new Set(
    projects
      .map(project => project.name.toLowerCase())
      .filter((name, index, all) => all.indexOf(name) !== index)
  );

  return (
    <InventoryUserListDialog
      open={open}
      onClose={onClose}
      title={`Projects using this skill (${projects.length})`}
      description={
        row ? `Projects where ${row.displayName} was seen.` : undefined
      }
      columns={[{ label: 'Project' }]}
      isLoading={false}
      error={null}
      isEmpty={projects.length === 0}
      emptyMessage="No projects found"
    >
      {projects.map(project => (
        <TableRow key={project.slug}>
          <TableCell className="font-medium break-all">
            {project.name}
            {clashing.has(project.name.toLowerCase()) && (
              <span className="block text-xs font-normal text-muted-foreground">
                {project.origin ?? project.slug}
              </span>
            )}
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}

export function SkillUsersModal({
  row,
  open,
  onClose,
}: SkillListModalProps): ReactElement {
  const users = row?.users ?? [];

  return (
    <InventoryUserListDialog
      open={open}
      onClose={onClose}
      title={`Users with this skill (${users.length})`}
      description={
        row ? `Users whose endpoints reported ${row.displayName}.` : undefined
      }
      columns={[{ label: 'Name' }, { label: 'Email' }]}
      isLoading={false}
      error={null}
      isEmpty={users.length === 0}
      emptyMessage="No users found"
    >
      {/* Both halves are nullable in the contract, so neither is a safe key
          and neither can be rendered raw. */}
      {users.map((user, index) => (
        <TableRow key={`${user.email ?? ''}-${index}`}>
          <TableCell className="font-medium break-all">
            {user.name ?? '—'}
          </TableCell>
          <TableCell className="break-all">{user.email ?? '—'}</TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}

SkillProjectsModal.displayName = 'SkillProjectsModal';
SkillUsersModal.displayName = 'SkillUsersModal';
